import { useEffect, useState } from "react";
import { Spinner, useToast } from "@chakra-ui/react";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import {
  CreateUpdatePaymentDetailInput,
  PaymentDetail,
  useCreatePaymentDetailMutation,
  useGetPaymentDetailLazyQuery,
  useUpdatePaymentDetailMutation,
} from "../../generated/graphql";
import { useErrorMessage } from "../../hooks/errorMessage";
import {
  FormMutationType,
  InheritFullWidthModalProps,
} from "../../components/FullWidthModal/interface";
import FullWidthModal from "../../components/FullWidthModal";
import MCFormPaymentDetail from "../../components/MCFormPaymentDetail";

const PaymentDetailForm: React.FC<
  InheritFullWidthModalProps<PaymentDetail>
> = ({ id, onClose, isOpen, ...props }) => {
  const [getPaymentDetail, { data, loading: isLoadingData }] =
    useGetPaymentDetailLazyQuery();

  useEffect(() => {
    if (data) {
      const { companyName, branchName, bankNo, swiftCode, accountNo, enable } =
        data?.getPaymentDetail as PaymentDetail;

      setPaymentDetailValue({
        companyName,
        branchName,
        bankNo,
        swiftCode,
        accountNo,
        enable,
      });
    }
  }, [data]);

  const { confirmation, setIsLoading, close } = useConfirmation();

  const toast = useToast();

  const { showErrorMessage } = useErrorMessage();

  const [paymentDetailValue, setPaymentDetailValue] =
    useState<CreateUpdatePaymentDetailInput>();
  const [createPaymentDetailMutation] = useCreatePaymentDetailMutation();
  const [updatePaymentDetailMutation] = useUpdatePaymentDetailMutation();

  useEffect(() => {
    if (id && isOpen) {
      getPaymentDetail({ variables: { id } });
    }
  }, [id, isOpen, getPaymentDetail]);

  const reset = () => {
    setPaymentDetailValue(undefined);
  };

  const savePaymentDetail = async (input: CreateUpdatePaymentDetailInput) => {
    try {
      setIsLoading(true);

      if (id) {
        const res = await updatePaymentDetailMutation({
          variables: {
            id,
            input,
          },
        });
        toast({
          description: "PaymentDetail successfully updated!",
          status: "success",
        });
        onClose({
          type: FormMutationType.UPDATE,
          data: res.data?.updatePaymentDetail as PaymentDetail,
        });
      } else {
        await createPaymentDetailMutation({
          variables: {
            input,
          },
        });
        toast({
          description: "Payment Detail successfully added!",
          status: "success",
        });
        onClose({ type: FormMutationType.CREATE });
      }

      reset();
    } catch (e) {
      showErrorMessage(e);
    } finally {
      setIsLoading(false);
      close();
    }
  };

  return (
    <FullWidthModal
      title={id ? "Edit Payment Detail" : "New Payment Detail"}
      onClose={() => {
        reset();
        onClose();
      }}
      isOpen={isOpen}
      {...props}
    >
      {isLoadingData ? (
        <Spinner />
      ) : (
        <MCFormPaymentDetail
          value={paymentDetailValue}
          onBack={() => {
            onClose();
          }}
          onSubmit={(value) => {
            confirmation({
              description: "Are you sure want to save?",
              callback: () => {
                savePaymentDetail(value);
              },
            });
          }}
        />
      )}
    </FullWidthModal>
  );
};

export default PaymentDetailForm;
